import { useRef, useState } from 'react';

import { DragEndEvent, DragMoveEvent, DragStartEvent } from '@dnd-kit/core';

import { getIdFromUrlPath } from '@shared/helpers';
import { IS_DRAGGING_CLASS } from '@shared/model/globals';
import {
    useCreateBudgetItem,
    useUpdateBudgetItem,
    useCreateExcessItem,
    useSetItemProceed,
} from '@widgets/OrdersRecognition/api/hooks';
import { dispatcher } from '@widgets/OrdersRecognition/lib/dispatcher';
import { ActiveDrag } from '@widgets/OrdersRecognition/model/types';
import {
    ORDERS_RECOGNITION_DATA_ATTR,
    ORDERS_RECOGNITION_DATA_ATTR_VALUE,
} from '@widgets/OrdersRecognition/model/сonsts';

type Coords = { x: number; y: number };
type Rect = { left: number; top: number; right: number; bottom: number };

const pointInRect = (p: Coords, r: Rect) =>
    p.x >= r.left && p.x <= r.right && p.y >= r.top && p.y <= r.bottom;

type Options = {
    onEnterSidebar?: () => void;
    onLeaveSidebar?: () => void;
};

export const useDragItems = (opts?: Options) => {
    const [activeDrag, setActiveDrag] = useState<ActiveDrag>(null);

    const startRef = useRef<Coords | null>(null);
    const pointerRef = useRef<Coords | null>(null);
    const sidebarRectRef = useRef<Rect | null>(null);
    const isInsideRef = useRef<boolean | null>(null);
    const rafRef = useRef<number | null>(null);
    const activeDropzoneRef = useRef<HTMLElement | null>(null);
    const isDraggingRef = useRef(false);

    const updateBudgetItemMutation = useUpdateBudgetItem();
    const createBudgetItemMutation = useCreateBudgetItem();
    const createExcessItemMutation = useCreateExcessItem();
    const setItemProceedMutation = useSetItemProceed();

    const handleGlobalPointerMove = (e: PointerEvent | MouseEvent) => {
        pointerRef.current = { x: e.clientX, y: e.clientY };
    };

    const setActiveDropzone = (next: HTMLElement | null) => {
        const prev = activeDropzoneRef.current;
        if (prev === next) return;

        if (prev) prev.classList.remove('is-drag-over');
        if (next) next.classList.add('is-drag-over');

        activeDropzoneRef.current = next;
    };

    const clearDropzoneHover = () => setActiveDropzone(null);

    const measureSidebar = (): Rect | null => {
        const el = document.querySelector(
            `[${ORDERS_RECOGNITION_DATA_ATTR}="${ORDERS_RECOGNITION_DATA_ATTR_VALUE}"]`
        ) as HTMLElement | null;

        if (!el) return null;

        const r = el.getBoundingClientRect();
        return { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
    };

    const afterDragEnd = () => {
        clearDropzoneHover();
        setActiveDrag(null);
        document.documentElement.classList.remove(IS_DRAGGING_CLASS);

        isDraggingRef.current = false;

        window.removeEventListener('pointermove', handleGlobalPointerMove, true);
        window.removeEventListener('mousemove', handleGlobalPointerMove as any, true);

        startRef.current = null;
        pointerRef.current = null;
        sidebarRectRef.current = null;
        isInsideRef.current = null;

        opts?.onEnterSidebar?.();

        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
    };

    const handleDragStart = (event: DragStartEvent) => {
        const data = event.active.data.current as any;
        if (!data?.type) return;
        setActiveDrag({ type: data.type, item: data.item });
        document.documentElement.classList.add(IS_DRAGGING_CLASS);

        isDraggingRef.current = true;

        const e = event.activatorEvent as MouseEvent | PointerEvent;
        if ('clientX' in e && 'clientY' in e) {
            const start = { x: e.clientX, y: e.clientY };
            startRef.current = start;
            pointerRef.current = start;
        }

        window.addEventListener('pointermove', handleGlobalPointerMove, true);
        window.addEventListener('mousemove', handleGlobalPointerMove as any, true);

        const rect = measureSidebar();
        const p = pointerRef.current;
        if (rect && p) {
            const inside = pointInRect(p, rect);
            isInsideRef.current = inside;
            inside ? opts?.onEnterSidebar?.() : opts?.onLeaveSidebar?.();
        } else {
            isInsideRef.current = null;
        }
    };

    const handleDragMove = (_event: DragMoveEvent) => {
        const p = pointerRef.current;
        if (!p) return;

        const el = document.elementFromPoint(p.x, p.y) as HTMLElement | null;
        const dropzone = el?.closest?.('[data-dropzone]') as HTMLElement | null;
        setActiveDropzone(dropzone);

        if (rafRef.current) cancelAnimationFrame(rafRef.current);

        rafRef.current = requestAnimationFrame(() => {
            const p2 = pointerRef.current;
            if (!p2) return;

            const rect = measureSidebar();
            if (!rect) return;

            const insideNow = pointInRect(p2, rect);
            const insidePrev = isInsideRef.current;

            if (insidePrev === null || insideNow !== insidePrev) {
                isInsideRef.current = insideNow;
                insideNow ? opts?.onEnterSidebar?.() : opts?.onLeaveSidebar?.();
            }
        });
    };

    const handleDragEnd = async (_event: DragEndEvent) => {
        const dropzone = activeDropzoneRef.current;
        const checks = Array?.from(
            dropzone?.querySelectorAll<HTMLElement>(
                '[data-position-check-guid]:not([data-position-guid])'
            ) ?? []
        );
        const priceEl = dropzone?.querySelector<HTMLElement>('.purchase-price');
        const amountEl = dropzone?.querySelector<HTMLElement>('.purchase-amount');
        let needCreate = false;
        if (priceEl && amountEl) {
            const priceValue = Number(priceEl?.dataset?.value);
            const amountValue = Number(amountEl?.dataset?.value);
            needCreate = priceValue > 0 || amountValue > 0;
        }
        const position = dropzone?.querySelector<HTMLElement>('[data-position-guid]');
        const excessTab = document?.querySelector('#nav-excess-tab');
        const inExcessTab = excessTab?.ariaSelected === 'true';
        const positionNameEl = dropzone?.querySelector<HTMLElement>('[data-value]');
        const positionGuid = position?.dataset?.positionGuid;
        const positionCheckGuid = position?.dataset?.positionCheckGuid;
        const positionName = positionNameEl?.dataset?.value;

        const itemData = {
            amount: activeDrag.type === 'order' ? 1 : activeDrag.item.amount,
            name: activeDrag.type === 'position' ? activeDrag.item.name : positionName,
            price: activeDrag.type === 'order' ? activeDrag.item.sum : activeDrag.item.price,
            supplier: activeDrag.item.supplier,
            comment: activeDrag.type === 'order' ? activeDrag.item.name : activeDrag.item.comment,
            unit: activeDrag.type === 'position' ? activeDrag.item.unit : null,
            documentDate: activeDrag?.item?.documentDate,
        };

        if (dropzone) {
            const hasChecks = checks.length > 0;

            dispatcher.positionDroppedLoader(true);
            if (inExcessTab) {
                const estimateId = getIdFromUrlPath('estimate');

                const createExcessItem = await createExcessItemMutation.mutateAsync({
                    estimateId,
                    data: {
                        ...itemData,
                        name:
                            activeDrag.type === 'order'
                                ? activeDrag.item.displayStringForExcess
                                : activeDrag.item.name,
                    },
                });

                const payload = createExcessItem.data.data;
                dispatcher.excessChanged({
                    payload,
                    hasCommentsFromClient: Boolean(
                        (activeDrag.type === 'order'
                            ? activeDrag.item.name
                            : activeDrag.item.comment ?? ''
                        ).trim()
                    ),
                    documentDate: activeDrag?.item?.documentDate,
                });
                dispatcher.positionDroppedLoader();

                afterDragEnd();
                await setItemProceedMutation.mutateAsync({
                    itemType: activeDrag.type,
                    itemId: activeDrag?.item?.id,
                });
                return;
            }
            if (needCreate || hasChecks) {
                const createBudgetRes = await createBudgetItemMutation.mutateAsync({
                    positionCheckGuid,
                    positionGuid,
                    data: itemData,
                });
                afterDragEnd();

                const payload = createBudgetRes?.data?.data;

                dispatcher.positionDroppedCreate({
                    positionGuid,
                    createdCheckGuid: payload.guid,
                    updatedData: payload,
                    documentDate: activeDrag?.item?.documentDate,
                });

                await setItemProceedMutation.mutateAsync({
                    itemType: activeDrag.type,
                    itemId: activeDrag?.item?.id,
                });

                dispatcher.positionDroppedLoader();

                return;
            }
            const updateBudgetRes = await updateBudgetItemMutation.mutateAsync({
                positionCheckGuid,
                positionGuid,
                data: itemData,
            });
            const payload = updateBudgetRes.data.data;

            dispatcher.positionDroppedUpdate({
                updatedCheckGuid: payload.guid,
                positionGuid: payload.position?.position_guid ?? positionGuid,
                updatedData: payload,
                supplierFromClient: activeDrag.item.supplier,
                documentDate: activeDrag?.item?.documentDate,
            });

            dispatcher.positionDroppedLoader();
            await setItemProceedMutation.mutateAsync({
                itemType: activeDrag.type,
                itemId: activeDrag?.item?.id,
            });
        } else {
            console.log('Dropped outside any dropzone');
        }

        afterDragEnd();
    };

    return { activeDrag, handleDragStart, handleDragMove, handleDragEnd };
};
